import { Add, Close, Inventory } from '@mui/icons-material';
import { ButtonGroup, Box, Button, Grid, TextField } from '@mui/material';
import { Field } from 'formik';
import React, { FC, Fragment } from 'react';
// Components
import { Card } from '../../../components';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { useSnackbar } from 'notistack';
import { CheckListMaterialProvided } from './ChecklistMaterialProvided';

interface IChecklistTemplatesMaterialsProvided {
  currentMaterialIndex: number | null;
  setFieldValue: (key: string, val: any) => void;
  values: any;
  setCurrentMaterialIndex: (val: number | null) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
}

export const ChecklistTemplatesMaterialsProvided: FC<IChecklistTemplatesMaterialsProvided> = ({
  currentMaterialIndex,
  setFieldValue,
  values,
  setCurrentMaterialIndex,
  handleBlur
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const materialsProvided = [...values.materialsProvided];
    const [removed] = materialsProvided.splice(result.source.index, 1);
    materialsProvided.splice(result.destination.index, 0, removed);

    setFieldValue('materialsProvided', materialsProvided);
  };
  return (
    <Card icon={<Inventory />} title="Materials Provided">
      {currentMaterialIndex === null && (
        <>
          <Box display="flex" justifyContent="flex-end" marginBottom="1rem">
            <Button
              type="button"
              startIcon={<Add />}
              onClick={() => {
                setFieldValue('materialsProvided', [
                  ...(values.materialsProvided || []),
                  {
                    description: '',
                    manufacturer: '',
                    model: ''
                  }
                ]);
                setCurrentMaterialIndex(
                  values.materialsProvided.length === 0 ? 0 : values.materialsProvided.length
                );
              }}
              variant="contained"
              color="secondary"
            >
              Add Material
            </Button>
          </Box>
          <DragDropContext onDragEnd={result => onDragEnd(result)}>
            <Droppable droppableId="work-item-droppable" direction="vertical">
              {(provided, snapshot) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {values.materialsProvided.map((mat, index) => {
                    return (
                      <Draggable
                        key={index}
                        draggableId={`work-item-${index}`}
                        index={index}
                        isDragDisabled={!!currentMaterialIndex}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <CheckListMaterialProvided
                              itemIndex={index}
                              setFieldValue={setFieldValue}
                              materialsProvided={values.materialsProvided}
                              material={mat}
                              setCurrentMaterialIndex={setCurrentMaterialIndex}
                              isDragging={snapshot.isDragging}
                            />
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </>
      )}
      {(currentMaterialIndex === 0 || currentMaterialIndex > 0) &&
        values.materialsProvided[currentMaterialIndex] && (
          <Grid container spacing={1}>
            <Grid item xs={12} sm={4}>
              <Field name={`materialsProvided.${currentMaterialIndex}.description`}>
                {({ field }) => {
                  return (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      variant="outlined"
                      autoComplete="nope"
                      label="Description"
                      size="small"
                      name={`materialsProvided.${currentMaterialIndex}.description`}
                      value={values.materialsProvided[currentMaterialIndex].description}
                      onBlur={handleBlur}
                    />
                  );
                }}
              </Field>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Field name={`materialsProvided.${currentMaterialIndex}.manufacturer`}>
                {({ field }) => {
                  return (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      autoComplete="nope"
                      label="Manufacturer"
                      size="small"
                      name={`materialsProvided.${currentMaterialIndex}.manufacturer`}
                      value={values.materialsProvided[currentMaterialIndex].manufacturer}
                      onBlur={handleBlur}
                    />
                  );
                }}
              </Field>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Field name={`materialsProvided.${currentMaterialIndex}.model`}>
                {({ field }) => {
                  return (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      autoComplete="nope"
                      label="Model"
                      size="small"
                      name={`materialsProvided.${currentMaterialIndex}.model`}
                      value={values.materialsProvided[currentMaterialIndex].model}
                      onBlur={handleBlur}
                    />
                  );
                }}
              </Field>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="flex-end">
                <ButtonGroup>
                  <Button
                    color="secondary"
                    variant="contained"
                    startIcon={<Add />}
                    disabled={!values.materialsProvided[currentMaterialIndex].description}
                    onClick={() => {
                      setCurrentMaterialIndex(null);
                      enqueueSnackbar(
                        `Material Provided Added, be sure to save the checklist template.`,
                        {
                          variant: 'success'
                        }
                      );
                    }}
                  >
                    Done
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Close />}
                    color="inherit"
                    onClick={() => {
                      if (!values.materialsProvided[currentMaterialIndex].materialsProvidedId) {
                        setCurrentMaterialIndex(null);
                        const newValues = [...values.materialsProvided];
                        newValues.pop();
                        setFieldValue(`materialsProvided`, newValues);
                      } else {
                        setCurrentMaterialIndex(null);
                      }
                    }}
                  >
                    Cancel
                  </Button>
                </ButtonGroup>
              </Box>
            </Grid>
          </Grid>
        )}
    </Card>
  );
};
